import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { inicioPiloto } from "@/lib/inicio";

// Ronda 18: mapa de módulo → primera ruta preferida cuando el usuario NO tiene
// Dashboard. Usado tanto por Navigate como por navigate() post-login.
const MODULO_FIRST_ROUTE = {
  pedidos: "/pedidos",
  fabricacion: "/fabricacion",
  empaque: "/empaque",
  entregas: "/entrega",
  facturacion: "/facturacion",
  conciliacion: "/conciliacion",
  reportes: "/reportes",
  plataforma_guias: "/plataforma-guias",
  inventario: "/inventario",
  gastos: "/gastos",
  marcaje: "/marcaje",
  clientes: "/clientes",
  productos: "/productos",
  usuarios: "/usuarios",
  cuentas: "/cuentas",
  domicilios: "/domicilios",
  cuadre: "/cuadre-repartidor",
  cierre_mes: "/cierre-mes",
  seguimiento: "/seguimiento",
  landing: "/landing-admin",
  dashboard: "/panel",
};

function firstAllowedRoute(user) {
  if (!user) return "/login";
  if (user.role === "piloto") return inicioPiloto(user);
  const mods = Array.isArray(user.modulos) ? user.modulos : [];
  if (mods.includes("dashboard") || user.role === "admin") return "/panel";
  // Orden preferido: primero producción/operaciones, luego admin.
  const preferido = ["pedidos", "fabricacion", "empaque", "entregas", "domicilios",
    "cuadre", "facturacion", "conciliacion", "reportes", "cuentas", "gastos",
    "marcaje", "inventario", "plataforma_guias", "productos", "clientes",
    "seguimiento", "landing", "cierre_mes", "usuarios"];
  for (const m of preferido) {
    if (mods.includes(m) && MODULO_FIRST_ROUTE[m]) return MODULO_FIRST_ROUTE[m];
  }
  // fallback: primer módulo con ruta conocida
  for (const m of mods) {
    if (MODULO_FIRST_ROUTE[m]) return MODULO_FIRST_ROUTE[m];
  }
  return "/login";
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("session_expired") === "1") {
        toast.info("Tu sesión expiró. Ingresá de nuevo.");
        sessionStorage.removeItem("session_expired");
      }
    } catch {}
  }, []);

  if (user && user !== false) {
    return <Navigate to={firstAllowedRoute(user)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (res.ok) {
      toast.success("Bienvenido");
      navigate(firstAllowedRoute(res.user));
    } else {
      toast.error(res.error || "No se pudo iniciar sesión");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-zinc-50">
      {/* Left: hero */}
      <div className="hidden lg:flex relative overflow-hidden bg-[#09090B] text-white p-12">
        <div className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(to right, #fff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="relative flex flex-col justify-between w-full">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-400 font-semibold">Ventas ERP · v1</div>
            <div className="mt-2 font-manrope font-black tracking-tight text-3xl">
              Hoja de <span className="text-[#7CA8FF]">Pedidos</span>
            </div>
          </div>

          <div className="max-w-lg">
            <h1 className="font-manrope font-black tracking-tight text-4xl xl:text-5xl leading-[1.05]">
              Un flujo, <br /> del pedido al pago.
            </h1>
            <p className="mt-6 text-zinc-300 text-sm leading-relaxed max-w-md">
              Catálogo con variantes, hoja de pedidos, fabricación, empaque, entrega y facturación —
              todo conectado en un solo tablero.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-3 text-center">
              {[
                { k: "01", v: "Pedido" },
                { k: "02", v: "Fabricación" },
                { k: "03", v: "Entrega" },
              ].map((s) => (
                <div key={s.k} className="border border-zinc-800 rounded-md p-3">
                  <div className="text-[10px] tracking-[0.28em] text-zinc-500 uppercase">{s.k}</div>
                  <div className="text-sm font-semibold mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] tracking-widest uppercase text-zinc-500">
            © {new Date().getFullYear()} · Hecho para tu equipo
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 font-semibold">Iniciar sesión</div>
            <h2 className="mt-2 font-manrope font-black tracking-tight text-3xl">Accede a tu panel</h2>
            <p className="mt-2 text-sm text-zinc-500">
              Ingresa con las credenciales de tu equipo.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-600">
                Correo
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11"
                autoComplete="email"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-600">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11"
                autoComplete="current-password"
                required
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-[#002FA7] hover:bg-[#00227A] text-white gap-2"
              data-testid="login-submit"
            >
              {busy ? "Ingresando..." : "Ingresar"} <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
